// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardFilterStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Dashboard Filter Store
 */
import dashboardFetcher from 'fetchers/Izenda.DashboardDataFetcher';
import reportDesignerFetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import {
  some,
  cloneDeep,
  find,
  findIndex,
  flow,
  forEach,
  get,
  includes,
  invoke,
  invokeArgs,
  isEmpty,
  isNil,
  map,
  pick,
  sortBy
} from 'lodash/fp';
import filterDetailsConstants from 'logic/data/Izenda.FilterDetailsConfigConstants';
import { INSERT, UPDATE } from 'logic/data/Izenda.ObjectStateConstants';
import CommonFilterField from 'logic/models/Dashboard/Izenda.CommonFilterField';
import FilterDetails from 'logic/models/Izenda.FilterDetails';
import FilterDetailsConfig from 'logic/models/Izenda.FilterDetailsConfig';
import ReportDesignerFilter from 'logic/models/Izenda.ReportDesignerFilter';
import { action, computed, observable, toJS } from 'mobx';

class DashboardFilterStore {
  @observable
  model = {
    reportFilter: new ReportDesignerFilter(),
    dashboardDefinition: null,
    // TODO: check this code can using decorator, getter when definerd plain object
    @computed
    get commonFilters() {
      return get('dashboard.commonFilterFields', this.dashboardDefinition);
    },
    // this is used for subscribe/schedule's filter setting values
    @computed
    get clonedFilters() {
      return flow(
        get('dashboard.commonFilterFields'),
        toJS,
        sortBy(item => item.position),
        map(item => new CommonFilterField(item))
      )(this.dashboardDefinition);
    }
  };

  @computed
  get reportFilter() {
    return this.model.reportFilter;
  }

  @computed
  get reportFilters() {
    return this.model.reportFilter;
  }

  constructor(dashboardDefinition) {
    this.model.dashboardDefinition = dashboardDefinition;
  }

  /**
   * Fetch filter operator group
   */
  fetchFilterOperatorGroup = () => reportDesignerFetcher.loadFilterOperatorGroup();

  /**
   * Fetch filter items.
   * @param {Number} version
   * @param {Array} filterValues
   * @param {Boolean} shouldUpdateFilterData
   * @param {Boolean} forCommon for comment filter.
   */
      getFilterData(
        version: number,
        filterValues: Array<any>,
        shouldUpdateFilterData: boolean,
        forCommon: boolean
      ): Array<any> {
    return this.fetchFilterOperatorGroup().then(filterOperatorGroup => {
      const commonData = {
        operatorGroup: filterOperatorGroup
      };

      return this.getConfigFieldList(commonData, filterValues, shouldUpdateFilterData, forCommon);
    });
  }

  /**
   * Calculate filter items using common filters.
   * @param {Object} commonData
   * @param {Array} subscriptionFilterFields
   * @param {Boolean} shouldUpdateFilterData
   * @param {Boolean} forCommon for comment filter.
   */
  getConfigFieldList(
    commonData: any,
    subscriptionFilterFields: Array<any>,
    shouldUpdateFilterData: boolean,
    forCommon: boolean
  ): Promise {
    const mapCommonFilterToFilterDetail = action('mapCommonFilterToFilterDetail', commonFilter => {
      let filterDetails = new FilterDetails();

      filterDetails.required = commonFilter.required;
      filterDetails.operatorId = commonFilter.operatorId;
      filterDetails.operatorSetting = commonFilter.operatorSetting;
      filterDetails.alias = commonFilter.displayName;
      filterDetails.name = commonFilter.name;
      filterDetails.dataType = commonFilter.dataType;
      filterDetails.value = commonFilter.value;
      filterDetails.id = commonFilter.id;
      filterDetails.visible = true;
      filterDetails.cascading = commonFilter.cascading;
      filterDetails.commonFilterRequired = some('required', commonFilter.filterFields);
      return filterDetails;
    });

    let listPromise = [];
    this.filters = forCommon ? this.model.commonFilters : this.model.clonedFilters;
    let {
      reportFilter: { configFieldList }
    } = this.model;

    this.model.reportFilter.configFieldList = this.filters.map(item => {
      const existingConfigField = find(configField => {
        return item.id === get('fieldDetails.id', configField);
      }, configFieldList);

      let filterDetails = mapCommonFilterToFilterDetail(item);
      let filterDetailsConfig = new FilterDetailsConfig(
        filterDetails,
        this.reportFilter.markUpdateResult.bind(this.reportFilter)
      );

      filterDetailsConfig.setFilterValue = filterValue => {
        FilterDetailsConfig.prototype.setFilterValue.call(filterDetailsConfig, filterValue);
        item.setFilterValue(filterDetailsConfig.fieldDetails.value);
        item.setOperatorValue(filterDetailsConfig.fieldDetails.operatorId);
      };

      filterDetailsConfig.commonData = commonData;

      if (!isNil(subscriptionFilterFields)) {
        let matchedSubscriptionFilterField = find(
          filter => filter.commonFilterFieldId === item.id,
          subscriptionFilterFields
        );

        if (!isNil(matchedSubscriptionFilterField)) {
          filterDetailsConfig.fieldDetails.operatorId = matchedSubscriptionFilterField.operatorId;
          filterDetailsConfig.fieldDetails.value = matchedSubscriptionFilterField.value;
        }
      }

      const getFilterDataPromise =
        !isNil(existingConfigField) && !shouldUpdateFilterData
          ? this.copyFilterValue(filterDetailsConfig, existingConfigField)
          : !filterDetailsConfig.doesSupportDataSourceLazyLoading
          ? this.getOperatorValueSource(filterDetailsConfig)
          : null;

      listPromise.push(getFilterDataPromise);

      return filterDetailsConfig;
    });

    return Promise.all(listPromise);
  }

  /**
   * Update cascading filter value for Dashboard
   * @param  {any} fieldDetail
   */
  @action
  updateReportDesignerCascadingFilter(fieldDetail: any) {
    const filterFields = this.reportFilter.configFieldList|| [];    
    const filterFields1 = filterFields.map(t => {
      return t.fieldDetails     
    });

    const filterIndex = findIndex(filterField => filterField.uniqueId === fieldDetail.uniqueId, filterFields1);

    if (filterIndex < 0) {
      return;
    }

    const isCascadingFilter = fieldDetail.cascading;
    if (isCascadingFilter) {
      for (let i = filterIndex + 1; i < filterFields1.length; i++) {
        // Do not reset value of parameter filter field
        !filterFields1[i].isParameter && filterFields1[i].updateByKey('value', '');

        if (!filterFields1[i].cascading) {
          break;
        }
      }
    }
  }

  /**
   * Copy filter value
   * @param {Object} configField
   * @param {Any} existingData
   */
  copyFilterValue(configField: any, existingConfigField: any): void {
    configField.populateExistingSourceValue(existingConfigField);
  }

  /**
   * Fetch operator value source.
   * @param {Object} configField
   */
  getOperatorValueSource(configField: any): Promise {
    let filterInfo = (configField && configField.filterInfo) || {};
    let fieldDetails = (configField && configField.fieldDetails) || {};
    let sourceType = filterInfo.sourceType;
    let dashboardDefinition = invoke('dashboardDefinition.getDashboardDefinition', this.model);

    // Remove first null item on array common filters
    if (dashboardDefinition.commonFilterFields && isNil(dashboardDefinition.commonFilterFields[0])) {
      dashboardDefinition.commonFilterFields = null;
    }

    let requestInfo = {
      dashboard: this.buildDashboardForGetFilterValue(dashboardDefinition),
      commonFilterName: fieldDetails.name
    };

    const populateData = response => {
      configField.populateOperatorSourceValue(sourceType, response);
    };

    switch (sourceType) {
      case 'inTimePeriod':
        return reportDesignerFetcher.loadTimeperiods().then(populateData.bind(this));
      case 'dataSource':
        return dashboardFetcher.loadFilterFieldData(requestInfo).then(populateData.bind(this));
      case 'dataSourceTree':
        return dashboardFetcher.loadFilterFieldDataAsTree(requestInfo).then(populateData.bind(this));
      case 'fieldSource':
      default:
        return dashboardFetcher.loadFilterDataSource(requestInfo).then(populateData.bind(this));
    }
  }

  /**
   * Get partial data for filter value
   * @param  {object} pageInfo
   * @param  {object} configField
   * @param  {string} searchKey
   * @param  {boolean} isSearching
   * @param  {array} subscriptionCommonFilterFields
   * @return {Promise}
   */
  getData(
    pageInfo: any,
    configField: any,
    searchKey: string,
    isSearching: boolean,
    subscriptionCommonFilterFields: Array<any>
  ): Promise {
    let fieldDetails = (configField && configField.fieldDetails) || {};
    let dashboardDefinition = invokeArgs(
      'dashboardDefinition.getDashboardDefinition',
      [false], // [forSaving]
      this.model
    );

    if (isNil(subscriptionCommonFilterFields)) {
      dashboardDefinition.commonFilterFields = toJS(this.filters);
      // Remove first null item on array common filters
      if (dashboardDefinition.commonFilterFields && isNil(dashboardDefinition.commonFilterFields[0])) {
        dashboardDefinition.commonFilterFields = null;
      }
    } else {
      let commonFilterFields = cloneDeep(toJS(this.filters));
      this.calculateCommonFilterFieldValues(commonFilterFields, subscriptionCommonFilterFields);
      dashboardDefinition.commonFilterFields = commonFilterFields;
    }

    let param = {
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      dashboard: this.buildDashboardForGetFilterValue(dashboardDefinition),
      commonFilterName: fieldDetails.name,
      includeAll: false,
      includeBlank: false,
      criteria: [
        {
          key: 'filter',
          value: searchKey
        }
      ]
    };

    let operatorKey = get('operator.key', configField.filterInfo);
    const operatorsSupportBlank = [
      'equals(popup)',
      'notequal(popup)',
      'equals(manualentry)',
      'equals(manualentrynoauto-complete)',
      'notequal(manualentry)',
      'notequal(manualentrynoauto-complete)',
      'equals(selection)',
      'notequal(selection)'
    ];
    if (includes(operatorKey, operatorsSupportBlank)) {
      param.includeBlank = true;
    }
    if (
      param.includeBlank &&
      configField.fieldDetails.operatorSetting === filterDetailsConstants.SELECT_SETTINGS.MULTIPLE
    ) {
      param.includeAll = true;
    }

    return dashboardFetcher
      .loadPartialFilterFieldData(param)
      .then(response => configField.updateOperatorSourceDisplay(response, isSearching));
  }

  /**
   * Update cascading filter value
   */
  @action('updateCascadingValue')
  updateCascadingValue(configFields: any): void {
    let configFieldList = this.model.reportFilter.configFieldList;
    let filterIndex = configFieldList.indexOf(configFields);

    if (filterIndex < 0) {
      return Promise.resolve();
    }
    const promiseList = [];
    let isCascadingFilter = configFields.fieldDetails.cascading;
    if (isCascadingFilter) {
      for (let i = filterIndex + 1; i < configFieldList.length; i++) {
        promiseList.push(this.getOperatorValueSource(configFieldList[i]));
        configFieldList[i].resetOperatorSourceDisplay();
        configFieldList[i].setFilterValue(null);

        if (!configFieldList[i].fieldDetails.cascading) {
          break;
        }
      }
    }
    return Promise.all(promiseList);
  }

  /**
   * Update cascading filter value (use for subscription/scheduler)
   * @param {Object} sourceFilter
   * @param {Array} configFieldList
   * @param {Array} subscriptionCommonFilterFields
   */
  @action('updateCascadingFilterValue')
  updateCascadingFilterValue(
    sourceFilter: any,
    configFieldList: Array<any>,
    subscriptionCommonFilterFields: Array<any>
  ): Promise {
    let filterIndex = configFieldList.indexOf(sourceFilter);

    if (filterIndex < 0) {
      return Promise.resolve();
    }

    if (sourceFilter.fieldDetails.cascading) {
      for (let i = filterIndex + 1; i < configFieldList.length; i++) {
        configFieldList[i].resetOperatorSourceDisplay();
        configFieldList[i].setFilterValue(null);
        let subscriptionCommonFilterField = find(
          { commonFilterFieldId: configFieldList[i].fieldDetails.id },
          subscriptionCommonFilterFields
        );

        if (!isNil(subscriptionCommonFilterField)) {
          subscriptionCommonFilterField.value = null;
          subscriptionCommonFilterField.state !== INSERT && (subscriptionCommonFilterField.state = UPDATE);
        }

        if (!configFieldList[i].fieldDetails.cascading) {
          break;
        }
      }
    }

    return Promise.resolve();
  }

  /**
   * Build common filter value to load data source of filter in subscription/scheduler
   * @param {Array} commonFilterFields
   * @param {Array} data
   */
  calculateCommonFilterFieldValues(commonFilterFields: Array<any>, data: Array<any>): void {
    // map value from subscription/scheduler filter into commonFilterFields
    // to load data source for filters of subscription/scheduler.
    forEach(filter => {
      let filterIndex = findIndex(item => item.id === filter.commonFilterFieldId, commonFilterFields);
      filterIndex !== -1 && commonFilterFields[filterIndex].setFilterValue(filter.value);
    }, data);
  }

  /**
   * Build dashboard detail for load data source of filter
   * @param {Object} dashboardDefinition
   */
  buildDashboardForGetFilterValue(dashboardDefinition: any): any {
    const cloneDashboardDefinition = toJS(dashboardDefinition);
    if (!isEmpty(cloneDashboardDefinition.dashboardParts)) {
      forEach(sub => {
        if (sub.state === INSERT) {
          sub.id = null;
        }
      }, cloneDashboardDefinition.subscriptions);
      return cloneDashboardDefinition;
    }
    return pick(['id', 'commonFilterFields'], dashboardDefinition);
  }
}

export default DashboardFilterStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.DashboardFilterStore.js